import React, { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from 'react-router-dom';
import GoBackButton from "../../layout/GoBackButton";
import { getSparepartExpense } from "../../../actions/sparepart_expense";
import MoneyFormat from "../../layout/MoneyFormat";
import Moment from "react-moment";
import moment from 'moment';
import { Badge } from 'react-bootstrap';

const SparepartExpensePage = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const { expense } = useSelector(state => state.sparepart_expense);

    useEffect(() => {
        dispatch(getSparepartExpense(id));
    }, [dispatch, id]);

    // Function to check if an expense has been updated
    const isExpenseUpdated = (expense) => {
        if (!expense?.created_at || !expense?.updated_at) return false;
        return moment(expense.created_at).format('YYYY-MM-DD HH:mm:ss') !== moment(expense.updated_at).format('YYYY-MM-DD HH:mm:ss');
    };

    // Function to get expense styling based on deletion/update status
    const getExpenseStyle = (expense) => {
        if (expense?.deleted_at) {
            return {
                backgroundColor: '#ffeeee',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                padding: '8px',
                margin: '4px 0'
            };
        } else if (isExpenseUpdated(expense)) {
            return {
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '8px',
                margin: '4px 0'
            };
        }
        return {};
    };

    // Function to get text styling for expense content
    const getExpenseTextStyle = (expense) => {
        if (expense?.deleted_at) {
            return {
                textDecoration: 'line-through',
                opacity: 0.7
            };
        }
        return {};
    };

    const formatNumber = (value) => {
        if (value || value === 0) {
            return Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return '0.00';
    };

    const getExpenseTypeLabel = (type) => {
        switch(type) {
            case 'transport':
                return 'Transport uchun';
            case 'machine':
                return 'Uskuna uchun';
            default:
                return type;
        }
    };

    return (
        <div>
            <main className="content mt-0 pt-0">
                <div className="container-fluid p-0">
                    <div className="row mb-2">
                        <div className="col-md-12 col-lg-8">
                            <h1 className="h3 mb-3">
                                <strong>Ehtiyot qism sarfi</strong> ma'lumotlari
                                {expense?.deleted_at && (
                                    <Badge bg="danger" className="ms-2">O'chirilgan</Badge>
                                )}
                                {isExpenseUpdated(expense) && (
                                    <Badge bg="warning" className="ms-2">O'zgartirilgan</Badge>
                                )}
                            </h1>
                        </div>
                        <div className="col-lg-4">
                            <div className="crud_actions text-end">
                                <GoBackButton />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12 col-lg-12">
                            <div className="card">
                                {/* Spare Part Information */}
                                <div className="card border-0 mb-3" style={getExpenseStyle(expense)}>
                                    <div className="card-header">
                                        <h5 className="card-title text-dark text-decoration-underline mb-0">
                                            Ehtiyot qism ma'lumotlari
                                        </h5>
                                    </div>
                                    <div className="card-body" style={getExpenseTextStyle(expense)}>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Nomi:</div>
                                            <div className="text-muted m-0">
                                                {expense?.spare_part ? (
                                                    <Link to={`/spare-parts/${expense.spare_part.id}`}>
                                                        {expense.spare_part.name}
                                                    </Link>
                                                ) : '-'}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">O'lchov birligi:</div>
                                            <div className="text-muted m-0">
                                                {expense?.spare_part?.unit?.name || '-'}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Sarflangan miqdor:</div>
                                            <div className="text-danger fw-bold m-0">
                                                -{expense?.quantity} {expense?.spare_part?.unit?.name}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Birlik narxi:</div>
                                            <div className="text-success fw-bold m-0">
                                                {expense?.unit_price ? (
                                                    <MoneyFormat value={expense.unit_price}/> 
                                                ) : '-'} so'm
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Jami summa:</div>
                                            <div className="text-success fw-bold m-0">
                                                <MoneyFormat value={expense?.total_amount}/> so'm
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Warehouse Information */}
                                <div className="card border-0 mb-3">
                                    <div className="card-header">
                                        <h5 className="card-title text-dark text-decoration-underline mb-0">
                                            Ombor ma'lumotlari
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Firma:</div>
                                            <div className="text-muted m-0">
                                                {expense?.warehouse?.firm ? (
                                                    <Link to={`/firms/${expense.warehouse.firm.id}`}>
                                                        {expense.warehouse.firm.name}
                                                    </Link>
                                                ) : '-'}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Ombor:</div>
                                            <div className="text-muted m-0">
                                                {expense?.warehouse?.name || '-'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Consumer Information */}
                                <div className="card border-0 mb-3">
                                    <div className="card-header">
                                        <h5 className="card-title text-dark text-decoration-underline mb-0">
                                            Sarflagan shaxs
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Ismi:</div>
                                            <div className="text-muted m-0">
                                                {expense?.consumed_by_user ? (
                                                    <Link to={`/users/${expense.consumed_by_user.id}`}>
                                                        {expense.consumed_by_user.name}
                                                    </Link>
                                                ) : '-'}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Email:</div>
                                            <div className="text-muted m-0">
                                                {expense?.consumed_by_user?.email || '-'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Transport/Machine Information */}
                                <div className="card border-0 mb-3">
                                    <div className="card-header">
                                        <h5 className="card-title text-dark text-decoration-underline mb-0">
                                            {getExpenseTypeLabel(expense?.expense_type)}
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {expense?.expense_type === 'transport' && expense?.transport && (
                                            <>
                                                <div className="d-flex align-items-center">
                                                    <div className="card-title m-2">Transport nomi:</div>
                                                    <div className="text-muted m-0">
                                                        <Link to={`/transports/${expense.transport.id}`}>
                                                            {expense.transport.name}
                                                        </Link>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <div className="card-title m-2">Davlat raqami:</div>
                                                    <div className="text-muted m-0">
                                                        {expense.transport.plate || '-'}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <div className="card-title m-2">Haydovchi:</div>
                                                    <div className="text-muted m-0">
                                                        {expense.transport.driver?.name || '-'}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {expense?.expense_type === 'machine' && expense?.machine && (
                                            <>
                                                <div className="d-flex align-items-center">
                                                    <div className="card-title m-2">Uskuna nomi:</div>
                                                    <div className="text-muted m-0">
                                                        <Link to={`/machines/${expense.machine.id}`}>
                                                            {expense.machine.name}
                                                        </Link>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <div className="card-title m-2">Model:</div>
                                                    <div className="text-muted m-0">
                                                        {expense.machine.model || '-'}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <div className="card-title m-2">Seriya raqami:</div>
                                                    <div className="text-muted m-0">
                                                        {expense.machine.serial_number || '-'}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <div className="card-title m-2">Operator:</div>
                                                    <div className="text-muted m-0">
                                                        {expense.machine.operator?.name || '-'}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {!expense?.transport && !expense?.machine && (
                                            <div className="text-muted">Ma'lumot mavjud emas</div>
                                        )}
                                    </div>
                                </div>

                                {/* General Information */}
                                <div className="card border-0 mb-3">
                                    <div className="card-header">
                                        <h4 className="card-title text-dark text-decoration-underline mb-0">
                                            Umumiy ma'lumotlar
                                        </h4>
                                    </div>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Ma'lumotnoma raqami:</div>
                                            <div className="text-muted m-0">
                                                {expense?.reference_number || expense?.id}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Sarf sanasi:</div>
                                            <div className="text-muted m-0">
                                                {expense?.date}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Yaratilgan:</div>
                                            <div className="text-muted m-0">
                                                <Moment date={expense?.created_at} format={'DD.MM.YYYY HH:mm'}/>
                                            </div>
                                        </div>
                                        {expense?.updated_at && isExpenseUpdated(expense) && (
                                            <div className="d-flex align-items-center">
                                                <div className="card-title m-2">Oxirgi o'zgartirish:</div>
                                                <div className="text-warning m-0">
                                                    <Moment date={expense.updated_at} format={'DD.MM.YYYY HH:mm'}/>
                                                </div>
                                            </div>
                                        )}
                                        {expense?.deleted_at && (
                                            <div className="d-flex align-items-center">
                                                <div className="card-title m-2">O'chirilgan:</div>
                                                <div className="text-danger m-0">
                                                    <Moment date={expense.deleted_at} format={'DD.MM.YYYY HH:mm'}/>
                                                </div>
                                            </div>
                                        )}
                                        <div className="d-flex align-items-center">
                                            <div className="card-title m-2">Izoh:</div>
                                            <div className="text-muted m-0">
                                                {expense?.notes || '-'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SparepartExpensePage; 